'use client';

import { useState } from "react";
import Modal from "./Modal";
import CustomButton from "../forms/CustomButton";
import useAddContactModal from "@/app/hooks/useAddContactModal";
import apiService from "@/app/services/apiService";
import { useRouter } from "next/navigation";

const AddContactModal = () => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    
    // Dados do formulário
    const [dataName, setDataName] = useState('');
    const [dataEmail, setDataEmail] = useState('');
    const [dataSubject, setDataSubject] = useState('');
    const [dataMessage, setDataMessage] = useState('');

    const AddContact = useAddContactModal();
    const router = useRouter();

    const resetForm = () => {
        setDataName('');
        setDataEmail('');
        setDataSubject('');
        setDataMessage('');
        setErrors([]);
    };

    const submitForm = async () => {
        setErrors([]);

        if (!dataName.trim() || !dataEmail.trim() || !dataMessage.trim()) {
            setErrors(['Por favor, preencha todos os campos obrigatórios.']);
            return;
        }

        if (dataMessage.trim().length < 10) {
            setErrors(['A mensagem deve ter pelo menos 10 caracteres.']);
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('name', dataName.trim());
            formData.append('email', dataEmail.trim().toLowerCase());
            formData.append('subject', dataSubject.trim() || 'Dúvida Geral');
            formData.append('message', dataMessage.trim());

            const response = await apiService.post('/api/contacts/create/', formData);

            if (response.success) {
                resetForm();
                AddContact.close();
                router.push('/?added=true');
            } else {
                const extractedErrors: string[] = [];
                if (response.errors) {
                    Object.keys(response.errors).forEach((key) => {
                        const fieldErrors = response.errors[key];
                        if (Array.isArray(fieldErrors)) {
                            fieldErrors.forEach((err: any) => extractedErrors.push(err.message || String(err)));
                        } else {
                            extractedErrors.push(String(fieldErrors));
                        }
                    });
                } else if (response.message) {
                    extractedErrors.push(response.message);
                } else {
                    extractedErrors.push('Erro ao enviar a mensagem. Tente novamente.');
                }
                setErrors(extractedErrors);
            }
        } catch (error) {
            setErrors(['Erro de conexão ao enviar a mensagem.']);
        } finally {
            setLoading(false);
        }
    };

    const content = (
        <div className="flex flex-col max-h-[75vh] sm:max-h-[80vh] overflow-y-auto px-1 py-2 sm:px-2">
            {/* Cabeçalho do Modal */}
            <div className="mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-400">Fale Conosco</h2>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                    Envie sua dúvida, sugestão ou solicitação de suporte. Responderemos no seu e-mail.
                </p>
            </div>

            {/* Alertas de Erro */}
            {errors.length > 0 && (
                <div className="mb-4 p-3 sm:p-4 bg-red-500/10 border border-red-500/50 rounded-xl space-y-1">
                    {errors.map((error, index) => (
                        <p key={index} className="text-xs sm:text-sm text-red-400 font-medium flex items-center gap-1">
                            <span>•</span> {error}
                        </p>
                    ))}
                </div>
            )}

            {/* Formulário */}
            <div className="space-y-4">
                {/* Nome e E-mail em Grid (Lado a lado no Desktop, Empilhado no Mobile) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Nome */}
                    <div className="flex flex-col space-y-1.5">
                        <label className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Seu Nome <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Nome completo"
                            value={dataName}
                            onChange={(e) => setDataName(e.target.value)}
                            className="w-full p-3 text-sm sm:text-base bg-gray-900/90 border border-gray-700/80 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-nat-reggaex focus:ring-1 focus:ring-nat-reggaex transition"
                        />
                    </div>

                    {/* E-mail */}
                    <div className="flex flex-col space-y-1.5">
                        <label className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Seu E-mail <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            placeholder="seuemail@exemplo.com"
                            value={dataEmail}
                            onChange={(e) => setDataEmail(e.target.value)}
                            className="w-full p-3 text-sm sm:text-base bg-gray-900/90 border border-gray-700/80 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-nat-reggaex focus:ring-1 focus:ring-nat-reggaex transition"
                        />
                    </div>
                </div>

                {/* Assunto */}
                <div className="flex flex-col space-y-1.5">
                    <label className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Assunto <span className="text-gray-500 font-normal">(Opcional)</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Ex: Dúvida sobre ingressos, parceria..."
                        value={dataSubject}
                        onChange={(e) => setDataSubject(e.target.value)}
                        className="w-full p-3 text-sm sm:text-base bg-gray-900/90 border border-gray-700/80 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-nat-reggaex focus:ring-1 focus:ring-nat-reggaex transition"
                    />
                </div>

                {/* Mensagem */}
                <div className="flex flex-col space-y-1.5">
                    <label className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Mensagem <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        rows={3}
                        placeholder="Escreva sua mensagem com detalhes..."
                        value={dataMessage}
                        onChange={(e) => setDataMessage(e.target.value)}
                        className="w-full p-3 text-sm sm:text-base bg-gray-900/90 border border-gray-700/80 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-nat-reggaex focus:ring-1 focus:ring-nat-reggaex resize-none transition min-h-[90px] sm:min-h-[110px]"
                    />
                </div>
            </div>

            {/* Botão de Envio */}
            <div className="pt-5 mt-auto">
                <CustomButton
                    label={loading ? "Enviando mensagem..." : "Enviar Mensagem 📤"}
                    onClick={submitForm}
                    disabled={loading}
                    className={`w-full py-3.5 sm:py-4 bg-nat-reggaex hover:bg-nat-reggaex-dark text-white text-center font-bold text-sm sm:text-base rounded-xl transition shadow-lg ${
                        loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer active:scale-[0.98]"
                    }`}
                />
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={AddContact.isOpen}
            close={() => {
                resetForm();
                AddContact.close();
            }}
            label="Contato"
            content={content}
        />
    );
};

export default AddContactModal;
